import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useMatch } from 'react-router';
import * as styles from './SidebarRow.module.scss';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import addTestId from '../utils/addTestId';

interface SidebarRowRightContentProps {
  customMenu?: React.ReactNode;
  expandState: 'collapsed' | 'expanded' | 'none';
  onExpandClick?: React.MouseEventHandler;
}

const stopEvent = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

export const SidebarRowRightContent = ({
  customMenu,
  expandState,
  onExpandClick,
}: SidebarRowRightContentProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const handleExpandClick = (e: React.MouseEvent) => {
    if (onExpandClick) {
      stopEvent(e);
      onExpandClick(e);
    }
  };

  return (
    <div className={styles.rightContent}>
      {!selectedSectionId && !!customMenu && (
        <div className={styles.customMenuContainer}>{customMenu}</div>
      )}
      {expandState !== 'none' && (
        <div
          className={classNames(styles.caretContainer)}
          onClick={handleExpandClick}
        >
          <Caret
            direction={expandState === 'expanded' ? 'up' : 'down'}
            size={10}
          />
        </div>
      )}
    </div>
  );
};

const isElementTruncated = (el: HTMLElement): boolean => {
  const range = document.createRange();
  range.selectNodeContents(el);
  const elementRect = el.getBoundingClientRect();
  const rangeRect = range.getBoundingClientRect();
  return rangeRect.width > elementRect.width;
};

type SidebarRowProps = {
  rightContent?: React.ReactNode;
  children: string;
  publicVariant?: boolean;
  type?: 'post' | 'draft' | 'archived' | 'topic' | 'template';
  level?: number;
  to?: string;
  onClick?: React.MouseEventHandler;
  onlyActiveOnIndex?: boolean;
  active?: boolean;
  menuItems?: React.ReactNode;
  icon: React.ReactNode;
};

const SidebarRow = ({
  icon,
  rightContent,
  children,
  publicVariant,
  type,
  level = 0,
  to,
  onClick,
  onlyActiveOnIndex,
  active,
  menuItems,
}: SidebarRowProps) => {
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const pathMatch = useMatch(to ?? '');
  const isActive = pathMatch != null || active;

  if (isTruncated && isActive) {
    setIsTruncated(false);
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    stopEvent(e);
    setIsMenuOpen(!isMenuOpen);
  };

  const content = (
    <Tooltip
      asChild
      placement="right"
      content={
        isTruncated ? (
          <div className={styles.contentTooltip}>{children}</div>
        ) : undefined
      }
      offset={12}
    >
      <div
        style={
          {
            '--sidebar-row-level': level,
          } as React.CSSProperties
        }
        className={classNames(styles.contentContainer, {
          [styles.isMenuOpen]: isMenuOpen,
        })}
        onMouseEnter={() => {
          if (textContainerRef.current) {
            setIsTruncated(isElementTruncated(textContainerRef.current));
          }
        }}
      >
        {!!icon && (
          <div className={styles.icon}>
            {typeof icon === 'string' ? (
              <SVGIcon size="regular">{icon}</SVGIcon>
            ) : (
              icon
            )}
          </div>
        )}
        {!!menuItems && (
          <div className={styles.menuContainer} onClick={stopEvent}>
            <OverflowMenu
              button={
                <div
                  className={styles.menuButtonContainer}
                  onClick={handleMenuClick}
                  {...addTestId('sidebarRowMenuButton')}
                >
                  <div className={styles.menuButton}>
                    <SVGIcon size={16}>{overflowMenuIcon}</SVGIcon>
                  </div>
                </div>
              }
              onOpenChange={setIsMenuOpen}
              open={isMenuOpen}
              placement="bottom-start"
            >
              {menuItems}
            </OverflowMenu>
          </div>
        )}
        <div
          className={classNames(styles.content, {
            [styles.isPublicVariant]: publicVariant,
          })}
        >
          <div className={styles.textContainer} ref={textContainerRef}>
            {children}
          </div>
        </div>
        {rightContent}
      </div>
    </Tooltip>
  );

  const isClickable = !!to || !!onClick;
  const className = classNames(styles.container, {
    [styles.active]: active,
    [styles.post]: type === 'post',
    [styles.topic]: type === 'topic',
    [styles.template]: type === 'template',
    [styles.archived]: type === 'archived',
    [styles.clickable]: isClickable,
  });

  return (
    <div className={styles.rowContainer}>
      {isClickable ? (
        <Button
          to={to}
          onClick={(event) => {
            if (selectedSectionId) {
              event.preventDefault();
            }
            onClick?.(event);
          }}
          activeClassName={styles.active}
          onlyActiveOnIndex={onlyActiveOnIndex}
          className={className}
        >
          {content}
        </Button>
      ) : (
        <div className={className}>{content}</div>
      )}
    </div>
  );
};

export default SidebarRow;
